import { FormEvent, useEffect, useState } from "react";
import { Edit, Plus, RefreshCw, Save, Truck, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "./AdminLayout";
import {
  AdminShippingMethod,
  AdminShippingMethodPayload,
  AdminApiError,
  AdminUser,
  EcpayLogisticsSubType,
  LogisticsProvider,
  ShippingDeliveryType,
  deleteAdminShippingMethod,
  getCurrentAdmin,
  listAdminShippingMethods,
  logout,
  saveAdminShippingMethod,
} from "./api";
import { useConfirmDialog } from "./components/ConfirmDialog";
import { AdminSwitchCard } from "./components/AdminChoiceControl";

type FormState = {
  Code: string;
  Name: string;
  Description: string;
  DeliveryType: ShippingDeliveryType;
  LogisticsProvider: LogisticsProvider | "";
  LogisticsSubType: EcpayLogisticsSubType | "";
  IsLogisticsCollection: boolean;
  RequiresStoreMap: boolean;
  BaseFee: string;
  FreeShippingThreshold: string;
  IsActive: boolean;
  SortOrder: string;
};

const emptyForm: FormState = {
  Code: "",
  Name: "",
  Description: "",
  DeliveryType: "HomeDelivery",
  LogisticsProvider: "",
  LogisticsSubType: "",
  IsLogisticsCollection: false,
  RequiresStoreMap: false,
  BaseFee: "0",
  FreeShippingThreshold: "",
  IsActive: true,
  SortOrder: "100",
};

const inputClass = "h-11 w-full rounded-md border border-[#cfd8d2] px-3 font-normal outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15";

const deliveryTypeLabels: Record<ShippingDeliveryType, string> = {
  HomeDelivery: "宅配",
  StorePickup: "門市自取",
  ConvenienceStorePickup: "超商取貨",
};

const logisticsSubTypeLabels: Record<EcpayLogisticsSubType, string> = {
  UNIMART: "7-11 B2C",
  FAMI: "全家 B2C",
  HILIFE: "萊爾富 B2C",
  UNIMARTC2C: "7-11 C2C",
  FAMIC2C: "全家 C2C",
  HILIFEC2C: "萊爾富 C2C",
  OKMARTC2C: "OK 超商 C2C",
};

function itemToForm(item: AdminShippingMethod): FormState {
  return {
    Code: item.Code,
    Name: item.Name,
    Description: item.Description || "",
    DeliveryType: item.DeliveryType || "HomeDelivery",
    LogisticsProvider: item.LogisticsProvider || "",
    LogisticsSubType: item.LogisticsSubType || "",
    IsLogisticsCollection: item.IsLogisticsCollection || false,
    RequiresStoreMap: item.RequiresStoreMap || false,
    BaseFee: String(item.BaseFee),
    FreeShippingThreshold: item.FreeShippingThreshold !== null ? String(item.FreeShippingThreshold) : "",
    IsActive: item.IsActive,
    SortOrder: String(item.SortOrder),
  };
}

export default function AdminShippingMethods() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState<AdminUser | null>(null);
  const [items, setItems] = useState<AdminShippingMethod[]>([]);
  const [loading, setLoading] = useState(true);
  const [pageMessage, setPageMessage] = useState("");
  const [editingItem, setEditingItem] = useState<AdminShippingMethod | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [formOpen, setFormOpen] = useState(false);
  const [formMessage, setFormMessage] = useState("");
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const { confirm, ConfirmDialog } = useConfirmDialog();

  async function load() {
    setLoading(true);
    setPageMessage("");
    try {
      const me = await getCurrentAdmin();
      setCurrentUser(me.User);
      const data = await listAdminShippingMethods();
      setItems(data.Items);
    } catch (error) {
      setPageMessage(error instanceof Error ? error.message : "配送方式讀取失敗。");
      navigate("/admin-login", { replace: true });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleLogout() {
    await logout().catch(() => undefined);
    navigate("/admin-login", { replace: true });
  }

  function openCreateForm() {
    setEditingItem(null);
    setForm(emptyForm);
    setFormMessage("");
    setFormErrors({});
    setFormOpen(true);
  }

  function openEditForm(item: AdminShippingMethod) {
    setEditingItem(item);
    setForm(itemToForm(item));
    setFormMessage("");
    setFormErrors({});
    setFormOpen(true);
  }

  function closeForm() {
    if (saving) return;
    setFormOpen(false);
    setEditingItem(null);
    setForm(emptyForm);
    setFormErrors({});
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFormErrors({});
    if (!form.Code.trim()) { setFormMessage("請輸入代碼。"); return; }
    if (!form.Name.trim()) { setFormMessage("請輸入配送方式名稱。"); return; }
    if (form.DeliveryType === "ConvenienceStorePickup" && form.RequiresStoreMap && (!form.LogisticsProvider || !form.LogisticsSubType)) {
      setFormMessage("要求電子地圖時，必須選擇物流商與超商物流類型。");
      return;
    }

    const payload: AdminShippingMethodPayload = {
      Code: form.Code.trim().toUpperCase(),
      Name: form.Name.trim(),
      Description: form.Description.trim() || undefined,
      DeliveryType: form.DeliveryType,
      LogisticsProvider: form.DeliveryType === "ConvenienceStorePickup" ? form.LogisticsProvider : "",
      LogisticsSubType: form.DeliveryType === "ConvenienceStorePickup" ? form.LogisticsSubType : "",
      IsLogisticsCollection: form.DeliveryType === "ConvenienceStorePickup" ? form.IsLogisticsCollection : false,
      RequiresStoreMap: form.DeliveryType === "ConvenienceStorePickup" ? form.RequiresStoreMap : false,
      BaseFee: Number(form.BaseFee || 0),
      FreeShippingThreshold: form.FreeShippingThreshold.trim() !== "" ? Number(form.FreeShippingThreshold) : null,
      IsActive: form.IsActive,
      SortOrder: Number(form.SortOrder || 100),
    };

    setSaving(true);
    setFormMessage("");
    try {
      await saveAdminShippingMethod(editingItem?.ShippingMethodId || null, payload);
      closeForm();
      await load();
    } catch (error) {
      if (error instanceof AdminApiError) {
        setFormErrors(error.errors);
        const detail = Object.values(error.errors).filter(Boolean).join("、");
        setFormMessage(detail ? `${error.message} ${detail}` : error.message);
      } else {
        setFormMessage(error instanceof Error ? error.message : "配送方式儲存失敗。");
      }
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(item: AdminShippingMethod) {
    const confirmed = await confirm({
      title: "刪除配送方式",
      description: <>確定要刪除 <strong className="font-bold text-[#203d32]">{item.Name}</strong> 嗎？</>,
      confirmText: "刪除",
      cancelText: "取消",
      tone: "danger",
    });
    if (!confirmed) return;

    try {
      await deleteAdminShippingMethod(item.ShippingMethodId);
      await load();
    } catch (error) {
      setPageMessage(error instanceof Error ? error.message : "配送方式刪除失敗。");
    }
  }

  return (
    <AdminLayout sectionLabel="網站設定" title="配送方式設定" user={currentUser} onLogout={handleLogout}>
      <p className="mb-4 text-xs text-[#7b877f]">
        設定前台結帳可使用的配送方式。宅配需要地址、門市自取會串營運據點，超商取貨可先人工必填門市資料，正式啟用後再要求電子地圖回傳門市代碼。
      </p>

      <div className="mb-4 flex justify-end gap-2">
        <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-5 font-bold text-[#315746] transition hover:bg-[#f0f5f2]" onClick={() => load()} type="button">
          <RefreshCw size={18} />
          重新整理
        </button>
        <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white transition hover:bg-[#203d32]" onClick={openCreateForm} type="button">
          <Plus size={20} />
          新增配送方式
        </button>
      </div>

      {pageMessage && <div className="mb-4 rounded-md border border-[#f2b8b5] bg-[#fdebea] px-4 py-3 text-sm font-medium text-[#8a1f1f]">{pageMessage}</div>}

      <div className="rounded-md border border-[#dfe6e1] bg-white">
        <div className="overflow-auto">
          <table className="w-full min-w-[980px] table-fixed border-collapse text-left">
            <thead className="bg-[#f0f3f1] text-sm text-[#59665f] shadow-[inset_0_-1px_0_#dfe6e1]">
              <tr>
                <th className="w-[230px] px-4 py-3">配送方式</th>
                <th className="w-[120px] px-4 py-3">類型</th>
                <th className="w-[150px] px-4 py-3">物流串接</th>
                <th className="w-[110px] px-4 py-3">基本運費</th>
                <th className="w-[150px] px-4 py-3">滿額免運</th>
                <th className="w-[90px] px-4 py-3">狀態</th>
                <th className="w-[70px] px-4 py-3">排序</th>
                <th className="w-[150px] px-4 py-3">操作</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td className="px-4 py-10 text-center text-[#59665f]" colSpan={8}>讀取中...</td></tr>
              ) : items.length === 0 ? (
                <tr><td className="px-4 py-10 text-center text-[#59665f]" colSpan={8}>尚未設定配送方式。</td></tr>
              ) : items.map((item) => (
                <tr className="border-t border-[#edf0ee]" key={item.ShippingMethodId}>
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-2">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#e9f4ec] text-[#245438]"><Truck size={16} /></span>
                      <div className="min-w-0">
                        <p className="truncate font-bold text-[#203d32]">{item.Name}</p>
                        <p className="text-xs text-[#9a8a7a]">{item.Code}</p>
                      </div>
                    </div>
                    {item.Description && <p className="mt-1 text-xs text-[#7b877f]">{item.Description}</p>}
                  </td>
                  <td className="px-4 py-4">{deliveryTypeLabels[item.DeliveryType]}</td>
                  <td className="px-4 py-4 text-sm text-[#59665f]">
                    {item.LogisticsProvider ? `${item.LogisticsProvider} / ${item.LogisticsSubType || "-"}` : "-"}
                    {item.RequiresStoreMap && <p className="text-xs text-[#9a6a1f]">要求電子地圖</p>}
                  </td>
                  <td className="px-4 py-4">{item.BaseFee > 0 ? `NT$ ${item.BaseFee.toLocaleString()}` : "免費"}</td>
                  <td className="px-4 py-4">{item.FreeShippingThreshold !== null ? `滿 NT$ ${item.FreeShippingThreshold.toLocaleString()} 免運` : "-"}</td>
                  <td className="px-4 py-4">
                    <span className={`inline-flex rounded-full px-3 py-1 text-xs font-bold ${item.IsActive ? "bg-[#e9f4ec] text-[#245438]" : "bg-[#eeeeee] text-[#59635d]"}`}>{item.IsActive ? "啟用" : "停用"}</span>
                  </td>
                  <td className="px-4 py-4">{item.SortOrder}</td>
                  <td className="px-4 py-4">
                    <div className="grid gap-2">
                      <button className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-3 font-bold text-[#315746] transition hover:bg-[#f0f5f2]" onClick={() => openEditForm(item)} type="button">
                        <Edit size={16} />
                        編輯
                      </button>
                      <button className="inline-flex h-10 items-center justify-center rounded-md border border-[#f2b8b5] bg-white px-3 font-bold text-[#b3261e] transition hover:bg-[#fdebea]" onClick={() => handleDelete(item)} type="button">
                        刪除
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {formOpen && (
        <div className="fixed inset-0 z-[70] flex items-end justify-center bg-[#10251c]/40 px-0 py-0 sm:items-center sm:px-4 sm:py-6">
          <button aria-label="關閉" className="absolute inset-0 cursor-default" onClick={closeForm} type="button" />
          <form className="relative flex max-h-[90vh] w-full max-w-[760px] flex-col overflow-hidden rounded-t-lg border border-[#dfe6e1] bg-white shadow-[0_22px_70px_rgba(32,61,50,.2)] sm:rounded-md" onSubmit={handleSubmit}>
            <div className="flex shrink-0 items-start justify-between gap-4 border-b border-[#edf0ee] px-5 py-4">
              <div>
                <h2 className="text-xl font-bold text-[#203d32]">{editingItem ? "編輯配送方式" : "新增配送方式"}</h2>
                <p className="mt-1 text-sm text-[#7b877f]">管理結帳頁顯示、運費與物流串接。</p>
              </div>
              <button className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-[#d8e0db] text-[#315746] transition hover:bg-[#f0f5f2]" onClick={closeForm} type="button">
                <X size={18} />
              </button>
            </div>

            <div className="grid gap-4 overflow-y-auto px-5 py-5">
              {formMessage && <div className="rounded-md border border-[#f2b8b5] bg-[#fdebea] px-4 py-3 text-sm font-medium text-[#8a1f1f]">{formMessage}</div>}

              <div className="grid gap-4 sm:grid-cols-2">
                <label className="grid gap-2 text-sm font-bold text-[#315746]">
                  <span>代碼 <span className="text-[#c3423f]">*</span></span>
                  <input className={inputClass} placeholder="HOME_DELIVERY" value={form.Code} onChange={(event) => setForm((current) => ({ ...current, Code: event.target.value.toUpperCase() }))} />
                  {formErrors.Code && <span className="text-xs font-medium text-[#b3261e]">{formErrors.Code}</span>}
                </label>
                <label className="grid gap-2 text-sm font-bold text-[#315746]">
                  <span>名稱 <span className="text-[#c3423f]">*</span></span>
                  <input className={inputClass} placeholder="宅配" value={form.Name} onChange={(event) => setForm((current) => ({ ...current, Name: event.target.value }))} />
                  {formErrors.Name && <span className="text-xs font-medium text-[#b3261e]">{formErrors.Name}</span>}
                </label>
              </div>

              <label className="grid gap-2 text-sm font-bold text-[#315746]">
                <span>配送類型 <span className="text-[#c3423f]">*</span></span>
                <select className={`${inputClass} bg-white`} value={form.DeliveryType} onChange={(event) => setForm((current) => ({ ...current, DeliveryType: event.target.value as ShippingDeliveryType }))}>
                  <option value="HomeDelivery">宅配</option>
                  <option value="StorePickup">門市自取</option>
                  <option value="ConvenienceStorePickup">超商取貨</option>
                </select>
              </label>

              <label className="grid gap-2 text-sm font-bold text-[#315746]">
                <span>說明</span>
                <input className={inputClass} placeholder="顯示在結帳頁的補充說明" value={form.Description} onChange={(event) => setForm((current) => ({ ...current, Description: event.target.value }))} />
              </label>

              {form.DeliveryType === "ConvenienceStorePickup" && (
                <section className="grid gap-4 rounded-md border border-[#dfe6e1] bg-[#f8fbf9] p-4">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <label className="grid gap-2 text-sm font-bold text-[#315746]">
                      <span>物流商</span>
                      <select className={`${inputClass} bg-white`} value={form.LogisticsProvider} onChange={(event) => setForm((current) => ({ ...current, LogisticsProvider: event.target.value as LogisticsProvider | "" }))}>
                        <option value="">暫不串接</option>
                        <option value="ECPay">綠界 ECPay</option>
                      </select>
                      {formErrors.LogisticsProvider && <span className="text-xs font-medium text-[#b3261e]">{formErrors.LogisticsProvider}</span>}
                    </label>
                    <label className="grid gap-2 text-sm font-bold text-[#315746]">
                      <span>超商物流類型</span>
                      <select className={`${inputClass} bg-white`} value={form.LogisticsSubType} onChange={(event) => setForm((current) => ({ ...current, LogisticsSubType: event.target.value as EcpayLogisticsSubType | "" }))}>
                        <option value="">請選擇</option>
                        {Object.entries(logisticsSubTypeLabels).map(([value, label]) => (
                          <option key={value} value={value}>{label}</option>
                        ))}
                      </select>
                      {formErrors.LogisticsSubType && <span className="text-xs font-medium text-[#b3261e]">{formErrors.LogisticsSubType}</span>}
                    </label>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <AdminSwitchCard checked={form.IsLogisticsCollection} description="貨到付款才啟用；若金流已先收款，請保持關閉。" label="物流代收" onChange={(IsLogisticsCollection) => setForm((current) => ({ ...current, IsLogisticsCollection }))} />
                    <AdminSwitchCard checked={form.RequiresStoreMap} description="啟用後正式流程必須由電子地圖回傳門市代碼，不能只手動輸入地址。" label="要求電子地圖" onChange={(RequiresStoreMap) => setForm((current) => ({ ...current, RequiresStoreMap }))} />
                  </div>
                </section>
              )}

              <div className="grid gap-4 sm:grid-cols-2">
                <label className="grid gap-2 text-sm font-bold text-[#315746]">
                  <span>基本運費</span>
                  <input className={inputClass} type="number" min={0} value={form.BaseFee} onChange={(event) => setForm((current) => ({ ...current, BaseFee: event.target.value }))} />
                  {formErrors.BaseFee && <span className="text-xs font-medium text-[#b3261e]">{formErrors.BaseFee}</span>}
                </label>
                <label className="grid gap-2 text-sm font-bold text-[#315746]">
                  <span>滿額免運門檻</span>
                  <input className={inputClass} type="number" min={0} placeholder="留空表示沒有滿額免運" value={form.FreeShippingThreshold} onChange={(event) => setForm((current) => ({ ...current, FreeShippingThreshold: event.target.value }))} />
                  {formErrors.FreeShippingThreshold && <span className="text-xs font-medium text-[#b3261e]">{formErrors.FreeShippingThreshold}</span>}
                </label>
              </div>

              <label className="grid gap-2 text-sm font-bold text-[#315746]">
                <span>排序</span>
                <input className={inputClass} type="number" value={form.SortOrder} onChange={(event) => setForm((current) => ({ ...current, SortOrder: event.target.value }))} />
              </label>
              <AdminSwitchCard checked={form.IsActive} description="停用後前台結帳不會顯示此配送方式。" label="啟用" onChange={(IsActive) => setForm((current) => ({ ...current, IsActive }))} />
            </div>

            <div className="flex shrink-0 flex-col-reverse gap-3 border-t border-[#edf0ee] px-5 py-4 sm:flex-row sm:justify-end">
              <button className="h-10 rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746] transition hover:bg-[#f0f5f2]" onClick={closeForm} type="button">
                取消
              </button>
              <button className="inline-flex h-10 items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white transition hover:bg-[#203d32] disabled:cursor-not-allowed disabled:opacity-60" disabled={saving} type="submit">
                <Save size={18} />
                {saving ? "儲存中" : "儲存"}
              </button>
            </div>
          </form>
        </div>
      )}

      <ConfirmDialog />
    </AdminLayout>
  );
}
